import React, { useEffect, useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar, LogIn, LogOut, Percent } from 'lucide-react';
import { useDashboardStore } from '../stores/dashboardStore';
import CommonActionBar from '../components/CommonActionBar';
import WeeklyOccupancyGrid from '../components/WeeklyOccupancyGrid';
import RoomDetailModal from '../components/RoomDetailModal';
import CreateBookingModal from '../components/CreateBookingModal';
import TodaysGuestsModal from '../components/TodaysGuestsModal';

const ReservationCalendar: React.FC = () => {
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showCreateBookingModal, setShowCreateBookingModal] = useState(false);
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [selectedRoomForBooking, setSelectedRoomForBooking] = useState<string>('');
  const [selectedDateForBooking, setSelectedDateForBooking] = useState<Date>(new Date());
  
  const {
    rooms,
    allBookings,
    calendarDates,
    selectedRoomId,
    isLoading,
    error,
    roomOrders,
    roomBookings,
    activeBooking,
    isLoadingRoomDetails,
    initializeCalendar,
    loadRooms,
    navigateCalendar,
    selectBooking,
    clearRoomSelection,
    getRoomById,
    createBooking,
  } = useDashboardStore();

  useEffect(() => {
    initializeCalendar();
    loadRooms();
  }, [initializeCalendar, loadRooms]);

  const handleBookingSelected = async (bookingId: number) => {
    await selectBooking(bookingId);
    setShowRoomModal(true);
  };

  const handleCloseModal = () => {
    setShowRoomModal(false);
    clearRoomSelection();
  };

  const handleRefresh = () => {
    loadRooms();
  };

  const handleEmptySpaceClicked = (roomId: string, date: Date) => {
    setSelectedRoomForBooking(roomId);
    setSelectedDateForBooking(date);
    setShowCreateBookingModal(true);
  };

  const handleCreateBooking = async (bookingData: any) => {
    try {
      await createBooking(bookingData);
      setShowCreateBookingModal(false);
      // Optionally show success message
    } catch (error) {
      console.error('Failed to create booking:', error);
      // Error handling is done in the modal
    }
  };

  const handleCloseCreateBookingModal = () => {
    setShowCreateBookingModal(false);
    setSelectedRoomForBooking('');
    setSelectedDateForBooking(new Date());
  };

  // Get today's check-ins
  const getTodaysCheckins = () => {
    const today = new Date();
    return allBookings.filter(booking => 
      isSameDay(new Date(booking.originalCheckinDate), today)
    );
  };

  // Get today's check-outs
  const getTodaysCheckouts = () => {
    const today = new Date();
    return allBookings.filter(booking => 
      isSameDay(new Date(booking.currentCheckoutDate), today)
    );
  };

  // Calculate occupancy percentage
  const getOccupancyPercentage = () => {
    const today = new Date();
    const occupiedRooms = allBookings.filter(booking => {
      const checkIn = new Date(booking.originalCheckinDate);
      const checkOut = new Date(booking.currentCheckoutDate);
      return today >= checkIn && today < checkOut;
    }).length;
    
    return rooms.length > 0 ? Math.round((occupiedRooms / rooms.length) * 100) : 0;
  };

  if (isLoading && rooms.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading rooms...</p>
        </div>
      </div>
    );
  }

  if (error && rooms.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">⚠️</span>
          </div>
          <h3 className="text-lg font-bold text-red-600 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if calendarDates has items before accessing
  if (!calendarDates || calendarDates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  const startDate = calendarDates[0];
  const endDate = calendarDates[calendarDates.length - 1];
  const weekRange = `${format(startDate, 'MMM dd')} - ${format(endDate, 'MMM dd')}`;

  return (
    <div className="space-y-6">
      {/* Common Action Bar */}
      <CommonActionBar
        showDateTime={false}
        leftActions={
          <div className="flex items-center space-x-3">
            {/* Today's Check-ins Button */}
            <button 
              onClick={() => setShowCheckinModal(true)}
              className="flex items-center space-x-2 px-3 py-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
            >
              <LogIn className="w-4 h-4" />
              <span className="text-sm font-medium">Today's Check-ins</span>
              <span className="bg-green-200 text-green-800 px-2 py-1 rounded-full text-xs font-bold">
                {getTodaysCheckins().length}
              </span>
            </button>

            {/* Today's Check-outs Button */}
            <button 
              onClick={() => setShowCheckoutModal(true)}
              className="flex items-center space-x-2 px-3 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-medium">Today's Check-outs</span>
              <span className="bg-orange-200 text-orange-800 px-2 py-1 rounded-full text-xs font-bold">
                {getTodaysCheckouts().length}
              </span>
            </button>

            {/* Occupancy Percentage Button (non-clickable) */}
            <div className="flex items-center space-x-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg">
              <Percent className="w-4 h-4" />
              <span className="text-sm font-medium">Occupancy</span>
              <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded-full text-xs font-bold">
                {getOccupancyPercentage()}%
              </span>
            </div>
          </div>
        }
        centerAlign={true}
        centerContent={
          <div className="flex items-center space-x-4">
            {/* Previous Week Button */}
            <button
              onClick={() => navigateCalendar(-1)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Previous</span>
            </button>

            {/* Date Range Display */}
            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-600" />
              <span className="text-lg font-medium text-gray-800">{weekRange}</span>
            </div>

            {/* Next Week Button */}
            <button
              onClick={() => navigateCalendar(1)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <span className="text-sm font-medium text-gray-700">Next</span>
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        }
        onRefresh={handleRefresh}
      />

      {/* Occupancy Grid */}
      <WeeklyOccupancyGrid
        rooms={rooms}
        calendarDates={calendarDates}
        selectedRoomId={selectedRoomId}
        onBookingSelected={handleBookingSelected}
        onEmptySpaceClicked={handleEmptySpaceClicked}
      />

      {/* Room Detail Modal */}
      {showRoomModal && (
        <RoomDetailModal
          room={getRoomById(selectedRoomId || '')}
          orders={roomOrders}
          bookings={roomBookings}
          activeBooking={activeBooking}
          isLoading={isLoadingRoomDetails}
          onClose={handleCloseModal}
        />
      )}

      {/* Create Booking Modal */}
      {showCreateBookingModal && (
        <CreateBookingModal
          isOpen={showCreateBookingModal}
          onClose={handleCloseCreateBookingModal}
          roomId={selectedRoomForBooking}
          selectedDate={selectedDateForBooking}
          onCreateBooking={handleCreateBooking}
        />
      )}

      {/* Today's Check-ins Modal */}
      <TodaysGuestsModal
        isOpen={showCheckinModal}
        onClose={() => setShowCheckinModal(false)}
        guests={getTodaysCheckins()}
        type="checkin"
        title="Today's Check-ins"
      />

      {/* Today's Check-outs Modal */}
      <TodaysGuestsModal
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        guests={getTodaysCheckouts()}
        type="checkout"
        title="Today's Check-outs"
      />
    </div>
  );
};

export default ReservationCalendar;

